/**
 * Smart voice matching for SpeechSynthesis with native-quality locale selection.
 * Prefers: 1) exact locale, 2) same language different region, 3) same base language, 4) last resort.
 * Never falls back to unrelated language if same-language voice exists.
 */
import { resolvePreferredSpeechLocales } from "./languageMapping";

function isNativeQualityVoice(v: SpeechSynthesisVoice): boolean {
  const n = (v.name || "").toLowerCase();
  return (
    n.includes("premium") ||
    n.includes("google") ||
    n.includes("natural") ||
    n.includes("enhanced") ||
    n.includes("online") ||
    (v as SpeechSynthesisVoice & { localService?: boolean }).localService === true
  );
}

function voiceLocale(v: SpeechSynthesisVoice): string {
  return (v.lang || "").trim().toLowerCase();
}

function baseLang(locale: string): string {
  return locale.split("-")[0];
}

/** Score for sorting: higher = better match. */
function scoreVoice(voice: SpeechSynthesisVoice, targetLocale: string, preferredLocales: string[]): number {
  const vLoc = voiceLocale(voice);
  const tLoc = targetLocale.toLowerCase();
  const vBase = baseLang(vLoc);
  const tBase = baseLang(tLoc);

  if (vLoc === tLoc) return 100;
  if (vBase !== tBase) return -1;

  const exactIdx = preferredLocales.findIndex((p) => p.toLowerCase() === vLoc);
  if (exactIdx >= 0) return 80 - exactIdx;

  const nativeBonus = isNativeQualityVoice(voice) ? 10 : 0;
  return 50 + nativeBonus;
}

/**
 * Selects the best available voice for the configured language/locale.
 * Fallback order: exact locale → same-language other region → same base language → null.
 */
export function resolveBestSpeechVoice(
  configuredLocale: string,
  availableVoices: SpeechSynthesisVoice[]
): SpeechSynthesisVoice | null {
  if (!availableVoices?.length || !configuredLocale?.trim()) return null;

  const targetLocale = configuredLocale.trim().toLowerCase();
  const preferredLocales = resolvePreferredSpeechLocales(configuredLocale).map((l) => l.toLowerCase());

  const scored = availableVoices
    .map((v) => ({ voice: v, score: scoreVoice(v, targetLocale, preferredLocales) }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score);

  return scored[0]?.voice ?? null;
}
